import { AbilityGate, IfAbility } from '@efdigital/ll-addon-auth';
import {
  applyCoachProfileChanges,
  coachDisplayName,
  CoachProfileReviewPanel,
  markCoachProfileDirty,
  pruneEmptyCoachProfileFragments,
  validateCoachProfile,
} from '@efdigital/ll-addon-coaching';
import { CoachProfileView } from '@efdigital/ll-addon-coaching';
import type { CoachProfile } from '@efdigital/ll-addon-data';
import { EditCoach } from '@efdigital/ll-admin-v2/components/coaching/edit-coach.gts';
import {
  EditContext,
  EditingStoppedReason,
} from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { deleteCoachProfile } from '@efdigital/ll-admin-v2/data/builders/xceed/coach-profiles';
import { deepSave } from '@efdigital/ll-admin-v2/data/deep-save';
import type { ModelFrom } from '@efdigital/ll-admin-v2/lib/type-utils';
import type CoachingCoachesCoachRoute from '@efdigital/ll-admin-v2/routes/coaching/coaches/coach';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { pageTitle } from 'ember-page-title';

interface CoachingCoachesCoachSignature {
  Args: {
    model: ModelFrom<CoachingCoachesCoachRoute>;
    controller: unknown;
  };
}

type CustomSection = 'pending' | 'published' | 'compare';

function eq(a: CustomSection | null, b: CustomSection): boolean {
  return a === b;
}

export default class CoachingCoachesCoachTemplate extends Component<CoachingCoachesCoachSignature> {
  @service declare store: Store;
  @service declare router: RouterService;

  @tracked customSection: CustomSection | null = this.args.model.pendingProfile
    ? 'compare'
    : 'published';

  approvePending = async () => {
    const published = this.args.model.publishedProfile;
    const pending = this.args.model.pendingProfile;

    if (!pending) {
      // This should never be called unless there is a pending profile
      return;
    }

    if (published) {
      applyCoachProfileChanges(published, pending);
      markCoachProfileDirty(published);
      await deepSave(published, this.store, { maxDepth: 1 });
      await this.store.request(deleteCoachProfile(pending));
    } else {
      pruneEmptyCoachProfileFragments(pending);
      const failures = validateCoachProfile(pending, { mode: 'admin' });
      if (failures.length > 0) {
        return;
      }

      pending.status = 'published';
      markCoachProfileDirty(pending);
      await deepSave(pending, this.store, { maxDepth: 1 });
    }

    this.customSection = 'published';

    await this.router.refresh('coaching.coaches.coach');
  };

  rejectPending = async () => {
    const pending = this.args.model.pendingProfile;
    if (!pending) {
      // This should never be called unless there is a pending profile
      return;
    }

    await this.store.request(deleteCoachProfile(pending));
    await this.router.refresh('coaching.coaches.coach');
  };

  editingStopped = async (reason: EditingStoppedReason) => {
    if (reason === EditingStoppedReason.Saved) {
      await this.router.refresh('coaching.coaches.coach');

      if (this.args.model.pendingProfile) {
        this.customSection = 'compare';
      }
    }
  };

  showCustomSection = (section: CustomSection) => {
    this.customSection = section;
    // eslint-disable-next-line ember/no-runloop
    next(() => {
      document
        .getElementById(`coachEditorCustomSection`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  hasMovedToSection = () => {
    this.customSection = null;
  };

  get isCustomSection(): boolean {
    return this.customSection !== null;
  }

  get profileToEdit(): CoachProfile | undefined {
    return this.args.model.pendingProfile || this.args.model.publishedProfile;
  }

  get coachUserId(): string | undefined {
    return this.profileToEdit?.user;
  }

  /** Opens the shared sessions list scoped to this coach. */
  get sessionsQuery() {
    return { coachUserId: this.coachUserId };
  }

  get editLabel(): string {
    if (this.args.model.pendingProfile) {
      return 'Amend draft';
    }
    return 'Edit published profile';
  }

  get saveLabel(): string {
    if (this.args.model.pendingProfile) {
      return 'Save draft';
    }
    return 'Save';
  }

  <template>
    {{pageTitle "Edit Coach"}}
    <div class="container py-5" data-test-coach-form>

      <IfAbility @ability="coaching-coaches" @atLeast="viewer">
        <:if>
          <AbilityGate @ability="coaching-coaches" as |perm|>
            {{#if this.profileToEdit}}
              {{! The page had no title, so nothing said whose profile this was
                  except the form fields. Names it, and gives the coach-scoped
                  actions somewhere to live other than the navigation column. }}
              <div
                class="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4"
              >
                <h1 class="h3 fw-bold mb-0">
                  {{coachDisplayName this.profileToEdit}}
                </h1>

                {{#if this.coachUserId}}
                  <LinkTo
                    @route="coaching.sessions"
                    @query={{this.sessionsQuery}}
                    class="btn btn-sm btn-outline-secondary"
                  >View sessions
                    <iconify-icon
                      inline
                      icon="ph:arrow-right"
                      aria-hidden="true"
                    /></LinkTo>
                {{/if}}
              </div>

              <EditContext
                @onEditingStopped={{this.editingStopped}}
                as |context|
              >
                <EditCoach
                  @coach={{this.profileToEdit}}
                  @context={{context}}
                  @canEdit={{perm.canEdit}}
                  @editLabel={{this.editLabel}}
                  @saveLabel={{this.saveLabel}}
                  @isCustomSection={{this.isCustomSection}}
                  @hasMovedToSection={{this.hasMovedToSection}}
                >
                  <:sidebar>
                    <LinkTo
                      @route="coaching.coaches.index"
                      class="btn btn-sm btn-outline-primary mb-4"
                    ><iconify-icon
                        inline
                        icon="ph:arrow-left"
                        aria-hidden="true"
                      />
                      Back to coaches</LinkTo>
                  </:sidebar>
                  <:customSectionNav as |buttonClass inactiveClass activeClass|>
                    {{#if @model.pendingProfile}}
                      <li class="nav-item">
                        <button
                          type="button"
                          class="{{buttonClass}}
                            {{if
                              (eq this.customSection 'compare')
                              activeClass
                              inactiveClass
                            }}"
                          {{on "click" (fn this.showCustomSection "compare")}}
                        >
                          <iconify-icon
                            inline
                            icon="ph:git-diff"
                            class="me-2"
                            aria-hidden="true"
                          />
                          Compare changes
                        </button>
                      </li>
                    {{/if}}

                    {{#if @model.publishedProfile}}
                      <li class="nav-item">
                        <button
                          type="button"
                          class="{{buttonClass}}
                            {{if
                              (eq this.customSection 'published')
                              activeClass
                              inactiveClass
                            }}"
                          {{on "click" (fn this.showCustomSection "published")}}
                        >
                          <iconify-icon
                            inline
                            icon="ph:user-circle"
                            class="me-2"
                            aria-hidden="true"
                          />
                          Profile &mdash;
                          {{#if context.isEditing}}
                            {{#if @model.pendingProfile}}
                              <em>published</em>
                            {{else}}
                              <em>editing</em>
                            {{/if}}
                          {{else}}
                            <em>published</em>
                          {{/if}}
                        </button>
                      </li>
                    {{/if}}

                    {{#if @model.pendingProfile}}
                      <li class="nav-item">
                        <button
                          type="button"
                          class="{{buttonClass}}
                            {{if
                              (eq this.customSection 'pending')
                              activeClass
                              inactiveClass
                            }}"
                          {{on "click" (fn this.showCustomSection "pending")}}
                        >
                          <iconify-icon
                            inline
                            icon="ph:user-circle"
                            class="me-2"
                            aria-hidden="true"
                          />
                          Profile &mdash;
                          <em>proposed</em>
                        </button>
                      </li>
                    {{/if}}
                  </:customSectionNav>
                  <:customSectionView as |isDisabled photoDraft|>
                    <div id="coachEditorCustomSection" class="pt-3">
                      {{#if (eq this.customSection "compare")}}
                        {{#if @model.pendingProfile}}
                          <CoachProfileReviewPanel
                            class="mb-4"
                            @onApprove={{if
                              perm.canEdit
                              (unless context.isEditing this.approvePending)
                            }}
                            @onReject={{if
                              perm.canEdit
                              (unless context.isEditing this.rejectPending)
                            }}
                            @published={{@model.publishedProfile}}
                            @pending={{@model.pendingProfile}}
                            @pendingPhotoDraft={{photoDraft}}
                          />
                        {{/if}}
                      {{else if (eq this.customSection "pending")}}
                        {{#if @model.pendingProfile}}
                          <CoachProfileView
                            @coach={{@model.pendingProfile}}
                            @photoDraft={{photoDraft}}
                          />
                        {{/if}}
                      {{else if (eq this.customSection "published")}}
                        {{#if @model.publishedProfile}}
                          <CoachProfileView
                            @coach={{@model.publishedProfile}}
                            @photoDraft={{unless
                              @model.pendingProfile
                              photoDraft
                            }}
                          />
                        {{else}}
                          <div class="alert alert-warning" role="alert">
                            <h4 class="alert-heading">No published profile</h4>
                            <p class="mb-0">The published profile is not
                              available.</p>
                          </div>
                        {{/if}}
                      {{/if}}
                    </div>
                  </:customSectionView>
                </EditCoach>
              </EditContext>
            {{else}}
              {{! A coach user id with no CoachProfile at all — reachable from a
                  session's "Coach profile" link when the coach never completed
                  one. The route resolves either way, so without this the page
                  was just blank past the header. }}
              <LinkTo
                @route="coaching.coaches.index"
                class="btn btn-sm btn-outline-primary mb-4"
              ><iconify-icon inline icon="ph:arrow-left" aria-hidden="true" />
                Back to coaches</LinkTo>
              <div class="alert alert-secondary" role="alert">
                <h4 class="alert-heading">No coach profile</h4>
                <p class="mb-0">This coach hasn&#39;t created a profile yet.</p>
              </div>
            {{/if}}
          </AbilityGate>
        </:if>
        <:else>
          <div class="alert alert-warning m-4" role="alert">
            <h4 class="alert-heading">Access Denied</h4>
            <p class="mb-0">You do not have permission to view coaches.</p>
          </div>
        </:else>
      </IfAbility>
    </div>
  </template>
}
